import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import axios from 'axios';

/*
 * ------------------------------------------------------------
 * OPPIES ADMIN - REVIEW MANAGER
 * ------------------------------------------------------------
 *
 * getAllReviews:
 * Returns:
 * {
 *   reviews: [...]
 * }
 *
 * deleteReview:
 * Expects:
 * {
 *   id,
 *   shopId
 * }
 *
 * Reviews container partition key: /shopId
 * ------------------------------------------------------------
 */

const FETCH_API =
    'https://oppiesfunc.azurewebsites.net/api/getAllReviews?code=7TQ8yp__NNNHg4b4PNW8qVJ6gnb1tX_G0dSpbowiLXXFAzFuUAVhyQ==';

const DELETE_API =
    'https://oppiesfunc.azurewebsites.net/api/deleteReview?code=WUzVf1Ai1dEKKARhSmAwfiHDRXBJYiL5vCaMJ24kmiHGAzFukqS9dg==';


function ReviewManager() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [search, setSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [selectedReview, setSelectedReview] = useState(null);

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [notice, setNotice] = useState('');

    const searchWrapRef = useRef(null);


    /*
     * ----------------------------------------------------------
     * LOAD ALL REVIEWS
     * ----------------------------------------------------------
     */

    useEffect(() => {
        fetchAllReviews();
    }, []);


    const fetchAllReviews = async () => {
        setLoading(true);
        setLoadError('');
        setNotice('');

        try {
            const response = await axios.get(FETCH_API);

            const list = Array.isArray(response.data)
                ? response.data
                : response.data?.reviews || [];

            /*
             * API should already return newest first.
             * Sort again here so the Admin page is always safe.
             */
            const sorted = [...list].sort((a, b) => {
                const aTime = a?.createdAt
                    ? new Date(a.createdAt).getTime()
                    : 0;

                const bTime = b?.createdAt
                    ? new Date(b.createdAt).getTime()
                    : 0;

                return bTime - aTime;
            });

            setReviews(sorted);
        } catch (error) {
            console.error(
                '❌ Failed to fetch reviews:',
                error
            );

            setLoadError(
                'Unable to load reviews. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };


    /*
     * ----------------------------------------------------------
     * CLOSE AUTOCOMPLETE WHEN CLICKING ELSEWHERE
     * ----------------------------------------------------------
     */

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                searchWrapRef.current &&
                !searchWrapRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener(
            'mousedown',
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleOutsideClick
            );
        };
    }, []);


    /*
     * ----------------------------------------------------------
     * SEARCH
     * ----------------------------------------------------------
     *
     * Search:
     * - Op Shop / Store name (shopId)
     * - OpShopper name
     * - OpShopper email
     * - Review comment
     * ----------------------------------------------------------
     */

    const filteredReviews = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return reviews;
        }

        return reviews.filter((review) => {
            const shop =
                String(review?.shopId || '').toLowerCase();

            const name =
                String(
                    review?.reviewerName || ''
                ).toLowerCase();

            const email =
                String(
                    review?.reviewerEmail || ''
                ).toLowerCase();

            const comment =
                String(
                    review?.comment || ''
                ).toLowerCase();

            return (
                shop.includes(query) ||
                name.includes(query) ||
                email.includes(query) ||
                comment.includes(query)
            );
        });
    }, [reviews, search]);


    /*
     * ----------------------------------------------------------
     * AUTOCOMPLETE SUGGESTIONS
     * ----------------------------------------------------------
     */

    const suggestions = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return [];
        }

        const resultMap = new Map();

        reviews.forEach((review) => {
            const shopId =
                String(review?.shopId || '').trim();

            const reviewerName =
                String(
                    review?.reviewerName || ''
                ).trim();

            const reviewerEmail =
                String(
                    review?.reviewerEmail || ''
                ).trim();


            /*
             * Shop / Store suggestion
             */
            if (
                shopId &&
                shopId.toLowerCase().includes(query)
            ) {
                const key =
                    `shop-${shopId.toLowerCase()}`;

                if (!resultMap.has(key)) {
                    resultMap.set(key, {
                        key,
                        value: shopId,
                        primary: formatShopName(shopId),
                        secondary: 'Op Shop / Store',
                        type: 'shop',
                    });
                }
            }


            /*
             * OpShopper suggestion
             */
            if (
                reviewerName &&
                (
                    reviewerName
                        .toLowerCase()
                        .includes(query) ||
                    reviewerEmail
                        .toLowerCase()
                        .includes(query)
                )
            ) {
                const key =
                    `user-${reviewerName.toLowerCase()}-${reviewerEmail.toLowerCase()}`;

                if (!resultMap.has(key)) {
                    resultMap.set(key, {
                        key,
                        value: reviewerName,
                        primary: reviewerName,
                        secondary:
                            reviewerEmail ||
                            'OpShopper',
                        type: 'user',
                    });
                }
            }
        });

        return Array
            .from(resultMap.values())
            .slice(0, 8);
    }, [reviews, search]);


    const chooseSuggestion = (suggestion) => {
        setSearch(suggestion.value);
        setShowSuggestions(false);
    };


    const clearSearch = () => {
        setSearch('');
        setShowSuggestions(false);
    };


    /*
     * ----------------------------------------------------------
     * REVIEW MODAL
     * ----------------------------------------------------------
     */

    const openReview = (review) => {
        setSelectedReview(review);
        setShowDeleteWarning(false);
        setNotice('');
    };


    const closeReview = () => {
        if (deleting) {
            return;
        }

        setSelectedReview(null);
        setShowDeleteWarning(false);
    };


    /*
     * ----------------------------------------------------------
     * DELETE REVIEW
     * ----------------------------------------------------------
     */

    const requestDelete = () => {
        setShowDeleteWarning(true);
    };


    const cancelDelete = () => {
        if (deleting) {
            return;
        }

        setShowDeleteWarning(false);
    };


    const confirmDelete = async () => {
        if (
            !selectedReview?.id ||
            !selectedReview?.shopId
        ) {
            return;
        }

        setDeleting(true);
        setNotice('');

        try {
            await axios.post(
                DELETE_API,
                {
                    id: selectedReview.id,
                    shopId: selectedReview.shopId,
                }
            );

            const deletedId =
                selectedReview.id;

            setReviews((current) =>
                current.filter(
                    (review) =>
                        review.id !== deletedId
                )
            );

            setShowDeleteWarning(false);
            setSelectedReview(null);

            setNotice(
                'Review deleted successfully.'
            );

            /*
             * Clear success message after a moment.
             */
            window.setTimeout(() => {
                setNotice('');
            }, 4000);

        } catch (error) {
            console.error(
                '❌ Failed to delete review:',
                error
            );

            setShowDeleteWarning(false);

            setNotice(
                'Unable to delete the review. Please try again.'
            );
        } finally {
            setDeleting(false);
        }
    };


    /*
     * ----------------------------------------------------------
     * RENDER
     * ----------------------------------------------------------
     */

    return (
        <div style={styles.page}>

            <div style={styles.container}>

                {/* ------------------------------------------------
                    PAGE HEADER
                ------------------------------------------------ */}

                <div style={styles.headerRow}>

                    <div>
                        <div style={styles.eyebrow}>
                            OPPIES ADMIN
                        </div>

                        <h1 style={styles.heading}>
                            Reviews
                        </h1>

                        <p style={styles.subheading}>
                            View and manage reviews submitted
                            across OPPIES.
                        </p>
                    </div>

                    <div style={styles.totalBox}>
                        <span style={styles.totalNumber}>
                            {reviews.length}
                        </span>

                        <span style={styles.totalLabel}>
                            Reviews
                        </span>
                    </div>

                </div>


                {/* ------------------------------------------------
                    SEARCH
                ------------------------------------------------ */}

                <div
                    ref={searchWrapRef}
                    style={styles.searchSection}
                >

                    <div style={styles.searchBox}>

                        <span style={styles.searchIcon}>
                            ⌕
                        </span>

                        <input
                            type="text"
                            value={search}
                            placeholder="Search by shop, OpShopper or review..."
                            onChange={(event) => {
                                setSearch(
                                    event.target.value
                                );

                                setShowSuggestions(true);
                            }}
                            onFocus={() => {
                                if (search.trim()) {
                                    setShowSuggestions(true);
                                }
                            }}
                            style={styles.searchInput}
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                style={styles.clearButton}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}

                    </div>


                    {/* AUTOCOMPLETE */}

                    {showSuggestions &&
                        search.trim() &&
                        suggestions.length > 0 && (

                            <div style={styles.suggestions}>

                                {suggestions.map(
                                    (suggestion) => (

                                        <button
                                            type="button"
                                            key={suggestion.key}
                                            onClick={() =>
                                                chooseSuggestion(
                                                    suggestion
                                                )
                                            }
                                            style={
                                                styles.suggestionRow
                                            }
                                        >

                                            <div
                                                style={
                                                    suggestion.type ===
                                                        'shop'
                                                        ? styles.shopSuggestionIcon
                                                        : styles.userSuggestionIcon
                                                }
                                            >
                                                {suggestion.type ===
                                                    'shop'
                                                    ? 'S'
                                                    : 'O'}
                                            </div>

                                            <div
                                                style={
                                                    styles.suggestionText
                                                }
                                            >
                                                <div
                                                    style={
                                                        styles.suggestionPrimary
                                                    }
                                                >
                                                    {
                                                        suggestion.primary
                                                    }
                                                </div>

                                                <div
                                                    style={
                                                        styles.suggestionSecondary
                                                    }
                                                >
                                                    {
                                                        suggestion.secondary
                                                    }
                                                </div>
                                            </div>

                                        </button>
                                    ))}

                            </div>
                        )}

                </div>


                {/* ------------------------------------------------
                    STATUS
                ------------------------------------------------ */}

                {notice && (
                    <div
                        style={{
                            ...styles.notice,
                            ...(notice
                                .toLowerCase()
                                .includes('unable')
                                ? styles.noticeError
                                : styles.noticeSuccess),
                        }}
                    >
                        {notice}
                    </div>
                )}


                {/* ------------------------------------------------
                    RESULTS HEADER
                ------------------------------------------------ */}

                {!loading && !loadError && (

                    <div style={styles.resultHeader}>

                        <div style={styles.resultCount}>
                            {search.trim()
                                ? `${filteredReviews.length} matching review${filteredReviews.length === 1
                                    ? ''
                                    : 's'
                                }`
                                : `${reviews.length} review${reviews.length === 1
                                    ? ''
                                    : 's'
                                }`}
                        </div>

                        <div style={styles.orderText}>
                            Newest first
                        </div>

                    </div>
                )}


                {/* ------------------------------------------------
                    LOADING
                ------------------------------------------------ */}

                {loading && (

                    <div style={styles.stateBox}>
                        <div style={styles.spinner} />
                        <div style={styles.stateTitle}>
                            Loading reviews...
                        </div>
                    </div>
                )}


                {/* ------------------------------------------------
                    LOAD ERROR
                ------------------------------------------------ */}

                {!loading && loadError && (

                    <div style={styles.stateBox}>

                        <div style={styles.errorIcon}>
                            !
                        </div>

                        <div style={styles.stateTitle}>
                            Couldn't load reviews
                        </div>

                        <div style={styles.stateText}>
                            {loadError}
                        </div>

                        <button
                            type="button"
                            onClick={fetchAllReviews}
                            style={styles.retryButton}
                        >
                            Try Again
                        </button>

                    </div>
                )}


                {/* ------------------------------------------------
                    EMPTY SEARCH
                ------------------------------------------------ */}

                {!loading &&
                    !loadError &&
                    filteredReviews.length === 0 && (

                        <div style={styles.stateBox}>

                            <div style={styles.emptyIcon}>
                                ☆
                            </div>

                            <div style={styles.stateTitle}>
                                No reviews found
                            </div>

                            <div style={styles.stateText}>
                                {search.trim()
                                    ? 'Try another shop or OpShopper name.'
                                    : 'There are no reviews in OPPIES yet.'}
                            </div>

                            {search.trim() && (
                                <button
                                    type="button"
                                    onClick={clearSearch}
                                    style={styles.retryButton}
                                >
                                    Clear Search
                                </button>
                            )}

                        </div>
                    )}


                {/* ------------------------------------------------
                    REVIEW LIST
                ------------------------------------------------ */}

                {!loading &&
                    !loadError &&
                    filteredReviews.length > 0 && (

                        <div style={styles.reviewList}>

                            {filteredReviews.map(
                                (review) => (

                                    <button
                                        type="button"
                                        key={review.id}
                                        onClick={() =>
                                            openReview(review)
                                        }
                                        style={styles.reviewCard}
                                    >

                                        <div
                                            style={
                                                styles.reviewTopRow
                                            }
                                        >

                                            <div
                                                style={
                                                    styles.reviewerArea
                                                }
                                            >

                                                <div
                                                    style={
                                                        styles.avatar
                                                    }
                                                >
                                                    {getInitial(
                                                        review
                                                            .reviewerName
                                                    )}
                                                </div>

                                                <div>

                                                    <div
                                                        style={
                                                            styles.reviewerName
                                                        }
                                                    >
                                                        {review
                                                            .reviewerName ||
                                                            'Anonymous'}
                                                    </div>

                                                    <div
                                                        style={
                                                            styles.shopName
                                                        }
                                                    >
                                                        {formatShopName(
                                                            review.shopId
                                                        )}
                                                    </div>

                                                </div>

                                            </div>


                                            <div
                                                style={
                                                    styles.dateArea
                                                }
                                            >
                                                {formatShortDate(
                                                    review.createdAt
                                                )}
                                            </div>

                                        </div>


                                        <div
                                            style={
                                                styles.starsRow
                                            }
                                        >
                                            <Stars
                                                rating={
                                                    review.rating
                                                }
                                            />

                                            <span
                                                style={
                                                    styles.ratingNumber
                                                }
                                            >
                                                {normaliseRating(
                                                    review.rating
                                                )}
                                                /5
                                            </span>
                                        </div>


                                        <div
                                            style={
                                                styles.commentPreview
                                            }
                                        >
                                            {truncateText(
                                                review.comment ||
                                                'No written comment.',
                                                180
                                            )}
                                        </div>


                                        <div
                                            style={
                                                styles.cardFooter
                                            }
                                        >

                                            <span
                                                style={
                                                    styles.viewText
                                                }
                                            >
                                                View review
                                            </span>

                                            <span
                                                style={
                                                    styles.chevron
                                                }
                                            >
                                                ›
                                            </span>

                                        </div>

                                    </button>
                                ))}

                        </div>
                    )}

            </div>


            {/* ====================================================
                REVIEW DETAILS MODAL
            ==================================================== */}

            {selectedReview && (

                <div
                    style={styles.modalOverlay}
                    onMouseDown={(event) => {
                        if (
                            event.target ===
                            event.currentTarget
                        ) {
                            closeReview();
                        }
                    }}
                >

                    <div style={styles.modal}>

                        <div style={styles.modalHeader}>

                            <div>
                                <div
                                    style={
                                        styles.modalEyebrow
                                    }
                                >
                                    REVIEW DETAILS
                                </div>

                                <div
                                    style={
                                        styles.modalTitle
                                    }
                                >
                                    Review
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={closeReview}
                                style={
                                    styles.modalCloseButton
                                }
                                aria-label="Close review"
                            >
                                ×
                            </button>

                        </div>


                        {/* RATING */}

                        <div style={styles.modalRating}>

                            <Stars
                                rating={
                                    selectedReview.rating
                                }
                                large
                            />

                            <span
                                style={
                                    styles.modalRatingNumber
                                }
                            >
                                {normaliseRating(
                                    selectedReview.rating
                                )} / 5
                            </span>

                        </div>


                        {/* REVIEW TEXT */}

                        <div style={styles.reviewTextBox}>
                            <div
                                style={
                                    styles.reviewText
                                }
                            >
                                {selectedReview.comment ||
                                    'No written comment was provided.'}
                            </div>
                        </div>


                        {/* DETAILS */}

                        <div style={styles.detailGrid}>

                            <DetailBlock
                                label="FROM"
                                primary={
                                    selectedReview.reviewerName ||
                                    'Anonymous'
                                }
                                secondary={
                                    selectedReview.reviewerEmail ||
                                    'No email recorded'
                                }
                            />

                            <DetailBlock
                                label="REVIEW FOR"
                                primary={
                                    selectedReview.shopName ||
                                    formatShopName(
                                        selectedReview.shopId
                                    )
                                }
                                secondary="Op Shop / Store"
                            />

                            <DetailBlock
                                label="SUBMITTED"
                                primary={formatFullDate(
                                    selectedReview.createdAt
                                )}
                            />

                            <DetailBlock
                                label="ADDRESS"
                                primary={
                                    selectedReview.shopAddress ||
                                    'Address not available'
                                }
                            />

                        </div>


                        {/* ACTIONS */}

                        <div style={styles.modalActions}>

                            <button
                                type="button"
                                onClick={closeReview}
                                style={styles.cancelButton}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={requestDelete}
                                style={styles.deleteButton}
                            >
                                Delete Review
                            </button>

                        </div>

                    </div>

                </div>
            )}


            {/* ====================================================
                DELETE CONFIRMATION MODAL
            ==================================================== */}

            {selectedReview &&
                showDeleteWarning && (

                    <div
                        style={styles.warningOverlay}
                    >

                        <div
                            style={
                                styles.warningModal
                            }
                        >

                            <div
                                style={
                                    styles.warningIcon
                                }
                            >
                                !
                            </div>

                            <div
                                style={
                                    styles.warningTitle
                                }
                            >
                                Delete this review?
                            </div>

                            <div
                                style={
                                    styles.warningText
                                }
                            >
                                This will permanently remove
                                the review from OPPIES.
                            </div>

                            <div
                                style={
                                    styles.warningSubtext
                                }
                            >
                                This action cannot be undone.
                            </div>


                            <div
                                style={
                                    styles.warningReview
                                }
                            >
                                <strong>
                                    {selectedReview
                                        .reviewerName ||
                                        'Anonymous'}
                                </strong>
                                {' • '}
                                {formatShopName(
                                    selectedReview.shopId
                                )}
                            </div>


                            <div
                                style={
                                    styles.warningActions
                                }
                            >

                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={
                                        cancelDelete
                                    }
                                    style={
                                        styles.warningCancel
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={
                                        confirmDelete
                                    }
                                    style={{
                                        ...styles.warningDelete,
                                        ...(deleting
                                            ? styles.disabledButton
                                            : {}),
                                    }}
                                >
                                    {deleting
                                        ? 'Deleting...'
                                        : 'Delete Review'}
                                </button>

                            </div>

                        </div>

                    </div>
                )}

        </div>
    );
}


/*
 * ==============================================================
 * SMALL COMPONENTS
 * ==============================================================
 */

function Stars({
    rating,
    large = false,
}) {
    const value = normaliseRating(rating);

    return (
        <span
            style={{
                ...styles.stars,
                ...(large
                    ? styles.starsLarge
                    : {}),
            }}
        >
            {[1, 2, 3, 4, 5].map(
                (star) => (
                    <span
                        key={star}
                        style={
                            star <= value
                                ? styles.starFilled
                                : styles.starEmpty
                        }
                    >
                        ★
                    </span>
                )
            )}
        </span>
    );
}


function DetailBlock({
    label,
    primary,
    secondary,
    small = false,
}) {
    return (
        <div style={styles.detailBlock}>

            <div style={styles.detailLabel}>
                {label}
            </div>

            <div
                style={{
                    ...styles.detailPrimary,
                    ...(small
                        ? styles.detailSmall
                        : {}),
                }}
            >
                {primary || '—'}
            </div>

            {secondary && (
                <div
                    style={
                        styles.detailSecondary
                    }
                >
                    {secondary}
                </div>
            )}

        </div>
    );
}


/*
 * ==============================================================
 * HELPERS
 * ==============================================================
 */

function normaliseRating(rating) {
    const value = Number(rating);

    if (!Number.isFinite(value)) {
        return 0;
    }

    return Math.min(
        5,
        Math.max(
            0,
            Math.round(value)
        )
    );
}


function getInitial(name) {
    const value =
        String(name || '').trim();

    return value
        ? value.charAt(0).toUpperCase()
        : '?';
}


function formatShopName(shopId) {
    if (!shopId) {
        return 'Unknown Op Shop / Store';
    }

    /*
     * shopId is currently based on the shop name.
     * Capitalise words for a cleaner Admin display.
     */
    return String(shopId)
        .split(' ')
        .filter(Boolean)
        .map((word) =>
            word.charAt(0).toUpperCase() +
            word.slice(1)
        )
        .join(' ');
}


function truncateText(text, maxLength) {
    const value =
        String(text || '').trim();

    if (value.length <= maxLength) {
        return value;
    }

    return (
        value.substring(
            0,
            maxLength
        ).trim() + '…'
    );
}


function formatShortDate(value) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return '';
    }

    return date.toLocaleDateString(
        'en-NZ',
        {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        }
    );
}


function formatFullDate(value) {
    if (!value) {
        return 'Unknown';
    }

    const date = new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return 'Unknown';
    }

    return date.toLocaleString(
        'en-NZ',
        {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }
    );
}


/*
 * ==============================================================
 * STYLES
 * ==============================================================
 */

const styles = {

    page: {
        minHeight: '100vh',
        background:
            'linear-gradient(180deg, #f8f6ee 0%, #f3f0e5 100%)',
        color: '#1d2a23',
        fontFamily:
            'Arial, Helvetica, sans-serif',
    },

    container: {
        width: '100%',
        maxWidth: 1050,
        margin: '0 auto',
        padding: '34px 24px 60px',
        boxSizing: 'border-box',
    },


    /*
     * HEADER
     */

    headerRow: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        marginBottom: 28,
    },

    eyebrow: {
        color: '#768479',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 2,
        marginBottom: 7,
    },

    heading: {
        margin: 0,
        color: '#173c2b',
        fontSize: 34,
        lineHeight: 1.1,
        fontWeight: 800,
    },

    subheading: {
        margin: '8px 0 0',
        color: '#69756d',
        fontSize: 14,
        lineHeight: 1.5,
    },

    totalBox: {
        minWidth: 105,
        padding: '12px 18px',
        borderRadius: 14,
        background: '#173c2b',
        color: '#fff',
        textAlign: 'center',
        boxShadow:
            '0 7px 20px rgba(23,60,43,0.14)',
    },

    totalNumber: {
        display: 'block',
        fontSize: 23,
        lineHeight: 1,
        fontWeight: 800,
    },

    totalLabel: {
        display: 'block',
        marginTop: 5,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 1.1,
        opacity: 0.78,
    },


    /*
     * SEARCH
     */

    searchSection: {
        position: 'relative',
        zIndex: 10,
        marginBottom: 22,
    },

    searchBox: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 54,
        background: '#ffffff',
        border: '1px solid #dedbcf',
        borderRadius: 14,
        boxShadow:
            '0 4px 15px rgba(0,0,0,0.045)',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },

    searchIcon: {
        width: 48,
        textAlign: 'center',
        color: '#7a887f',
        fontSize: 25,
        lineHeight: 1,
        transform: 'rotate(-20deg)',
    },

    searchInput: {
        flex: 1,
        height: '100%',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        color: '#233129',
        fontSize: 15,
        padding: 0,
    },

    clearButton: {
        width: 50,
        height: '100%',
        border: 'none',
        background: 'transparent',
        color: '#89928c',
        fontSize: 25,
        cursor: 'pointer',
    },

    suggestions: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 61,
        background: '#fff',
        border:
            '1px solid #dedbd0',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow:
            '0 15px 35px rgba(0,0,0,0.14)',
    },

    suggestionRow: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 12,
        padding: '12px 15px',
        border: 'none',
        borderBottom:
            '1px solid #efede5',
        background: '#fff',
        textAlign: 'left',
        cursor: 'pointer',
    },

    shopSuggestionIcon: {
        width: 34,
        height: 34,
        borderRadius: 17,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: '#e8efe8',
        color: '#27543d',
        fontSize: 12,
        fontWeight: 800,
    },

    userSuggestionIcon: {
        width: 34,
        height: 34,
        borderRadius: 17,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: '#f2ead9',
        color: '#8b7041',
        fontSize: 12,
        fontWeight: 800,
    },

    suggestionText: {
        minWidth: 0,
    },

    suggestionPrimary: {
        color: '#26342c',
        fontSize: 14,
        fontWeight: 700,
    },

    suggestionSecondary: {
        marginTop: 2,
        color: '#818a84',
        fontSize: 12,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },


    /*
     * RESULTS
     */

    resultHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 11,
        padding: '0 3px',
    },

    resultCount: {
        color: '#4c5b52',
        fontSize: 13,
        fontWeight: 700,
    },

    orderText: {
        color: '#929a95',
        fontSize: 12,
    },

    reviewList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
    },

    reviewCard: {
        width: '100%',
        display: 'block',
        padding: 0,
        overflow: 'hidden',
        border: '1px solid #e0ddd2',
        borderRadius: 14,
        background: '#fff',
        boxShadow:
            '0 3px 12px rgba(0,0,0,0.045)',
        textAlign: 'left',
        cursor: 'pointer',
        color: 'inherit',
    },

    reviewTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 20,
        padding: '16px 18px 0',
    },

    reviewerArea: {
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        minWidth: 0,
    },

    avatar: {
        width: 38,
        height: 38,
        borderRadius: 19,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#173c2b',
        color: '#fff',
        fontWeight: 800,
        fontSize: 15,
    },

    reviewerName: {
        color: '#26362d',
        fontSize: 15,
        fontWeight: 800,
    },

    shopName: {
        marginTop: 3,
        color: '#7a857e',
        fontSize: 12,
    },

    dateArea: {
        color: '#969d99',
        fontSize: 11,
        whiteSpace: 'nowrap',
        paddingTop: 3,
    },

    starsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '13px 18px 0',
    },

    stars: {
        display: 'inline-flex',
        gap: 2,
        fontSize: 16,
        lineHeight: 1,
    },

    starsLarge: {
        fontSize: 24,
        gap: 3,
    },

    starFilled: {
        color: '#b89b5e',
    },

    starEmpty: {
        color: '#dedbd3',
    },

    ratingNumber: {
        color: '#858e88',
        fontSize: 11,
        fontWeight: 700,
    },

    commentPreview: {
        padding: '11px 18px 16px',
        color: '#505c55',
        fontSize: 13,
        lineHeight: 1.55,
        whiteSpace: 'pre-line',
    },

    cardFooter: {
        minHeight: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop:
            '1px solid #f0eee7',
        padding: '0 18px',
        background: '#fdfcf8',
    },

    viewText: {
        color: '#728078',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.7,
    },

    chevron: {
        color: '#8c958f',
        fontSize: 24,
        lineHeight: 1,
    },


    /*
     * STATES
     */

    stateBox: {
        padding: '65px 20px',
        textAlign: 'center',
        background: '#fff',
        border:
            '1px solid #e0ddd2',
        borderRadius: 14,
    },

    spinner: {
        width: 28,
        height: 28,
        margin: '0 auto 16px',
        border:
            '3px solid #e1e4e1',
        borderTop:
            '3px solid #315840',
        borderRadius: '50%',
    },

    errorIcon: {
        width: 42,
        height: 42,
        margin: '0 auto 14px',
        borderRadius: 21,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f7e6e3',
        color: '#a93e32',
        fontWeight: 900,
        fontSize: 21,
    },

    emptyIcon: {
        color: '#bba875',
        fontSize: 40,
        marginBottom: 8,
    },

    stateTitle: {
        color: '#304037',
        fontSize: 16,
        fontWeight: 800,
    },

    stateText: {
        maxWidth: 420,
        margin: '7px auto 0',
        color: '#7d8781',
        fontSize: 13,
        lineHeight: 1.5,
    },

    retryButton: {
        marginTop: 18,
        padding: '9px 17px',
        border: 'none',
        borderRadius: 8,
        background: '#b9a16b',
        color: '#fff',
        fontWeight: 700,
        cursor: 'pointer',
    },


    /*
     * NOTICE
     */

    notice: {
        marginBottom: 17,
        padding: '11px 14px',
        borderRadius: 9,
        fontSize: 13,
        fontWeight: 700,
    },

    noticeSuccess: {
        background: '#e6f2e8',
        color: '#28613b',
        border:
            '1px solid #c8e0cc',
    },

    noticeError: {
        background: '#f8e9e6',
        color: '#9b4036',
        border:
            '1px solid #ebccc6',
    },


    /*
     * REVIEW MODAL
     */

    modalOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background:
            'rgba(19, 29, 23, 0.62)',
        backdropFilter: 'blur(3px)',
        boxSizing: 'border-box',
    },

    modal: {
        width: '100%',
        maxWidth: 650,
        maxHeight: '90vh',
        overflowY: 'auto',
        background: '#faf8f1',
        borderRadius: 19,
        boxShadow:
            '0 25px 70px rgba(0,0,0,0.30)',
    },

    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '22px 24px 18px',
        borderBottom:
            '1px solid #e5e1d6',
    },

    modalEyebrow: {
        color: '#89948d',
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 1.7,
    },

    modalTitle: {
        marginTop: 3,
        color: '#173c2b',
        fontSize: 25,
        fontWeight: 800,
    },

    modalCloseButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        border:
            '1px solid #dedbd1',
        background: '#fff',
        color: '#536158',
        fontSize: 24,
        lineHeight: '30px',
        cursor: 'pointer',
    },

    modalRating: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '20px 24px 5px',
    },

    modalRatingNumber: {
        color: '#68746d',
        fontSize: 13,
        fontWeight: 800,
    },

    reviewTextBox: {
        margin: '16px 24px 20px',
        padding: '18px 19px',
        borderRadius: 12,
        background: '#fff',
        border:
            '1px solid #e4e1d7',
    },

    reviewText: {
        color: '#39463f',
        fontSize: 14,
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap',
    },

    detailGrid: {
        display: 'grid',
        gridTemplateColumns:
            'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        padding: '0 24px 22px',
    },

    detailBlock: {
        minWidth: 0,
        padding: 14,
        borderRadius: 10,
        background: '#f2efe5',
    },

    detailLabel: {
        color: '#8c958f',
        fontSize: 9,
        fontWeight: 900,
        letterSpacing: 1.2,
        marginBottom: 6,
    },

    detailPrimary: {
        color: '#304038',
        fontSize: 13,
        fontWeight: 800,
        overflowWrap: 'anywhere',
    },

    detailSecondary: {
        marginTop: 4,
        color: '#77817b',
        fontSize: 11,
        overflowWrap: 'anywhere',
    },

    detailSmall: {
        fontSize: 10,
        fontFamily: 'monospace',
        fontWeight: 600,
    },

    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        padding: '17px 24px 22px',
        borderTop:
            '1px solid #e5e1d6',
        background: '#fffef9',
    },

    cancelButton: {
        minWidth: 105,
        padding: '10px 17px',
        borderRadius: 8,
        border:
            '1px solid #d5d3cb',
        background: '#fff',
        color: '#526058',
        fontWeight: 700,
        cursor: 'pointer',
    },

    deleteButton: {
        minWidth: 140,
        padding: '10px 17px',
        borderRadius: 8,
        border: 'none',
        background: '#ad3e35',
        color: '#fff',
        fontWeight: 800,
        cursor: 'pointer',
    },


    /*
     * DELETE WARNING
     */

    warningOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background:
            'rgba(13, 20, 16, 0.72)',
        boxSizing: 'border-box',
    },

    warningModal: {
        width: '100%',
        maxWidth: 430,
        padding: '28px 28px 24px',
        borderRadius: 17,
        background: '#fffdf8',
        boxShadow:
            '0 25px 70px rgba(0,0,0,0.34)',
        textAlign: 'center',
    },

    warningIcon: {
        width: 48,
        height: 48,
        margin: '0 auto 14px',
        borderRadius: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f6dfdb',
        color: '#a5382f',
        fontSize: 24,
        fontWeight: 900,
    },

    warningTitle: {
        color: '#2c3731',
        fontSize: 20,
        fontWeight: 800,
    },

    warningText: {
        marginTop: 10,
        color: '#626e67',
        fontSize: 13,
        lineHeight: 1.5,
    },

    warningSubtext: {
        marginTop: 3,
        color: '#9c4b43',
        fontSize: 12,
        fontWeight: 700,
    },

    warningReview: {
        marginTop: 18,
        padding: '11px 12px',
        borderRadius: 8,
        background: '#f5f2e9',
        color: '#5c6861',
        fontSize: 11,
        overflowWrap: 'anywhere',
    },

    warningActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: 10,
        marginTop: 22,
    },

    warningCancel: {
        flex: 1,
        padding: '10px 14px',
        borderRadius: 8,
        border:
            '1px solid #d6d3cb',
        background: '#fff',
        color: '#59665e',
        fontWeight: 700,
        cursor: 'pointer',
    },

    warningDelete: {
        flex: 1,
        padding: '10px 14px',
        borderRadius: 8,
        border: 'none',
        background: '#ad3e35',
        color: '#fff',
        fontWeight: 800,
        cursor: 'pointer',
    },

    disabledButton: {
        opacity: 0.55,
        cursor: 'not-allowed',
    },
};


export default ReviewManager;